import { DestroyRef, effect, inject, Injectable, Injector, signal, computed } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Title } from '@angular/platform-browser';
import { NavigationEnd, Router } from '@angular/router';
import { CommandCenterSecurityRight, InboxEventType, InboxRoomMessage } from '@jms/model';
import { FaviconService, Icon, SizedIcons } from '@jms/ui/ng-favicon';
import { RightsService } from '@jms/utils/util.permissions';
import { InboxNotificationSocketService } from '@workbuddy/modules/boards/inbox/notifications/services/inbox.notification.socket.service';
import { InboxQueueSocketService } from '@workbuddy/modules/boards/inbox/queues/services/inbox.queue.socket.service';
import { Subject, auditTime, filter, fromEvent, map, Observable, take } from 'rxjs';

type UnreadCountSyncPayload = { count: number; source: string; timestamp: number; };
type QueueAttentionSyncPayload = { count: number; source: string; timestamp: number; };

@Injectable({ providedIn: 'root' })
export class InboxBadgeService {

    private readonly inboxSocket = inject(InboxNotificationSocketService);
    private readonly inboxQueueSocket = inject(InboxQueueSocketService);
    private readonly favicon = inject(FaviconService);
    private readonly router = inject(Router);
    private readonly title = inject(Title);
    private readonly rights = inject(RightsService);
    private readonly destroyRef = inject(DestroyRef);
    private readonly injector = inject(Injector);
    private readonly MAX_DISPLAY_COUNT = 99;
    private readonly TITLE_COUNT_PREFIX_REGEX = /^\(\d+\+?\)\s+/;
    private readonly CROSS_TAB_CHANNEL_NAME = 'jms-inbox-unread-count';
    private readonly CROSS_TAB_STORAGE_KEY = 'jms:inbox:unread-count';
    private readonly ATTENTION_CROSS_TAB_CHANNEL_NAME = 'jms-inbox-attention';
    private readonly ATTENTION_CROSS_TAB_STORAGE_KEY = 'jms:inbox:attention';
    private readonly tabId = this.createTabId();
    private readonly UNREAD_DOT_CACHE_KEY = 'jms-inbox-unread-dot-v2';

    private crossTabChannel: BroadcastChannel | null = null;
    private attentionCrossTabChannel: BroadcastChannel | null = null;
    private readonly queueCountById = new Map<string, number>();
    private readonly queueNavigationInvalidated$ = new Subject<void>();

    private readonly storageSyncListener = (event: StorageEvent): void => {

        if (!event.newValue) {
            return;
        }

        try {
            if (event.key === this.CROSS_TAB_STORAGE_KEY) {
                const payload = JSON.parse(event.newValue) as UnreadCountSyncPayload;
                this.handleCrossTabPayload(payload);
            }
            if (event.key === this.ATTENTION_CROSS_TAB_STORAGE_KEY) {
                const payload = JSON.parse(event.newValue) as QueueAttentionSyncPayload;
                this.handleAttentionCrossTabPayload(payload);
            }
        } catch {
            return;
        }
    };

    public readonly $unreadCount = signal<number>(0);
    private readonly $queueAttentionCount = signal<number>(0);
    public readonly $attentionCount = computed(() => this.$unreadCount() + this.$queueAttentionCount());
    public readonly $hasAttention = computed(() => this.$attentionCount() > 0);

    public readonly $displayCount = computed(() => {

        const count = this.$attentionCount();
        if (count <= 0) return '';
        if (count > this.MAX_DISPLAY_COUNT) return `${this.MAX_DISPLAY_COUNT}+`;
        return count.toString();
    });

    constructor() {

        this.subscribeToSocketEvents();
        this.subscribeToQueueNavigationInvalidations();
        this.syncBrowserBadge();
        this.syncBrowserTitleOnNavigation();
        this.syncUnreadCountAcrossTabs();
    }

    public async loadUnreadCount(): Promise<void> {

        if (this.rights.hasRights(CommandCenterSecurityRight.CanViewInbox)) {
            try {
                const count = await this.inboxSocket.getUnreadCount();
                this.setUnreadCount(count ?? 0);
            } catch {
                this.setUnreadCount(0);
            }
        } else {
            this.setUnreadCount(0);
        }

        if (this.rights.hasRights(CommandCenterSecurityRight.CanViewQueues)) {
            try {
                const response = await this.inboxQueueSocket.getAll();
                this.queueCountById.clear();
                for (const queue of response.queues) {
                    this.queueCountById.set(queue._id, Math.max(0, queue.count ?? 0));
                }
                this.syncQueueAttentionCount();
            } catch {
                this.queueCountById.clear();
                this.setQueueAttentionCount(0);
            }
        } else {
            this.queueCountById.clear();
            this.setQueueAttentionCount(0);
        }
    }

    private syncBrowserBadge(): void {

        effect(() => {

            const attentionCount = this.$attentionCount();
            this.updateFaviconBadge(this.$hasAttention());
            this.updateNativeAppBadge(attentionCount);
            this.updateDocumentTitleBadge(attentionCount);
        }, { injector: this.injector });
    }

    private syncBrowserTitleOnNavigation(): void {

        this.router.events
            .pipe(
                filter(event => event instanceof NavigationEnd),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(() => {
                this.updateDocumentTitleBadge(this.$attentionCount());
            });
    }

    private subscribeToSocketEvents(): void {

        effect(() => {

            this.inboxSocket.events$
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe(event => this.handleSocketEvent(event));
        }, { injector: this.injector });
    }

    private subscribeToQueueNavigationInvalidations(): void {

        this.queueNavigationInvalidated$
            .pipe(auditTime(500), takeUntilDestroyed(this.destroyRef))
            .subscribe(() => void this.loadUnreadCount());
    }

    private handleSocketEvent(event: InboxRoomMessage): void {

        if (event.type === InboxEventType.UnreadCount && this.rights.hasRights(CommandCenterSecurityRight.CanViewInbox)) {
            this.setUnreadCount(event.payload.count);
        }

        if (event.type === InboxEventType.QueueNavigationInvalidated && this.rights.hasRights(CommandCenterSecurityRight.CanViewQueues)) {
            this.queueNavigationInvalidated$.next();
        }
    }

    private syncQueueAttentionCount(): void {

        this.setQueueAttentionCount(Array.from(this.queueCountById.values()).reduce((total, count) => total + count, 0));
    }

    private setUnreadCount(count: number, broadcast = true): void {

        const safeCount = Math.max(0, count ?? 0);
        this.$unreadCount.set(safeCount);

        if (broadcast) {
            this.broadcastUnreadCount(safeCount);
        }
    }

    private setQueueAttentionCount(count: number, broadcast = true): void {

        const safeCount = Math.max(0, count ?? 0);
        this.$queueAttentionCount.set(safeCount);

        if (broadcast) {
            this.broadcastAttention(safeCount);
        }
    }

    private updateFaviconBadge(hasAttention: boolean): void {

        if (!hasAttention) {
            this.favicon.setDefault();
            return;
        }

        this.favicon.setCustom((_, defaultIcons) => this.renderHighVisibilityUnreadDot(defaultIcons), {}, this.UNREAD_DOT_CACHE_KEY);
    }

    private updateNativeAppBadge(count: number): void {

        if (typeof navigator === 'undefined') {
            return;
        }

        const browserNavigator = navigator as Navigator & { setAppBadge?: (contents?: number) => Promise<void>; clearAppBadge?: () => Promise<void>; };
        if (!browserNavigator.setAppBadge || !browserNavigator.clearAppBadge) {
            return;
        }

        const safeCount = Math.max(0, count ?? 0);
        if (safeCount > 0) {
            void browserNavigator.setAppBadge(safeCount).catch(() => undefined);
            return;
        }

        void browserNavigator.clearAppBadge().catch(() => undefined);
    }

    private updateDocumentTitleBadge(count: number): void {

        const currentTitle = this.title.getTitle() || 'Workbuddy';
        const baseTitle = currentTitle.replace(this.TITLE_COUNT_PREFIX_REGEX, '');
        const safeCount = Math.max(0, count ?? 0);

        if (safeCount <= 0) {
            this.title.setTitle(baseTitle);
            return;
        }

        const displayCount = safeCount > this.MAX_DISPLAY_COUNT ? `${this.MAX_DISPLAY_COUNT}+` : safeCount.toString();
        this.title.setTitle(`(${displayCount}) ${baseTitle}`);
    }

    private renderHighVisibilityUnreadDot(defaultIcons: SizedIcons): Observable<Icon[]> {

        const icon = this.resolveBaseFavicon(defaultIcons);
        const image = new Image();
        image.src = icon.href;

        return fromEvent(image, 'load')
            .pipe(
                take(1),
                map((event: Event) => event.target as HTMLImageElement),
                map(loadedImage => {
                    const canvas = document.createElement('canvas');
                    canvas.width = loadedImage.width;
                    canvas.height = loadedImage.height;

                    const context = canvas.getContext('2d');
                    if (!context) {
                        return [icon];
                    }

                    context.drawImage(loadedImage, 0, 0);

                    const strokeWidth = Math.max(1, canvas.width * 0.035);
                    const radius = Math.min(canvas.width, canvas.height) * 0.2;
                    const margin = radius + strokeWidth / 2 + 1;
                    const centerX = canvas.width - margin;
                    const centerY = margin;

                    context.fillStyle = '#dc2626';
                    context.beginPath();
                    context.arc(centerX, centerY, radius, 0, Math.PI * 2, false);
                    context.closePath();
                    context.fill();

                    context.strokeStyle = '#ffffff';
                    context.lineWidth = strokeWidth;
                    context.beginPath();
                    context.arc(centerX, centerY, radius, 0, Math.PI * 2, false);
                    context.closePath();
                    context.stroke();

                    return [{ rel: icon.rel, type: icon.type, sizes: icon.sizes, href: canvas.toDataURL() }];
                })
            );
    }

    private resolveBaseFavicon(defaultIcons: SizedIcons): Icon {

        const icon = defaultIcons?.['32x32'] ?? Object.values(defaultIcons ?? {})[0];
        if (icon) {
            return icon;
        }

        return { href: '/favicon/cropped-WorkBuddy-favicon-32x32.png', rel: 'icon', type: 'image/png', sizes: '32x32' };
    }

    private syncUnreadCountAcrossTabs(): void {

        if (typeof window === 'undefined') {
            return;
        }

        if (typeof BroadcastChannel !== 'undefined') {
            this.crossTabChannel = new BroadcastChannel(this.CROSS_TAB_CHANNEL_NAME);
            this.crossTabChannel.onmessage = (event: MessageEvent<UnreadCountSyncPayload>) => {
                this.handleCrossTabPayload(event.data);
            };
            this.attentionCrossTabChannel = new BroadcastChannel(this.ATTENTION_CROSS_TAB_CHANNEL_NAME);
            this.attentionCrossTabChannel.onmessage = (event: MessageEvent<QueueAttentionSyncPayload>) => {
                this.handleAttentionCrossTabPayload(event.data);
            };
        }

        window.addEventListener('storage', this.storageSyncListener);

        this.destroyRef.onDestroy(() => {
            this.crossTabChannel?.close();
            this.crossTabChannel = null;
            this.attentionCrossTabChannel?.close();
            this.attentionCrossTabChannel = null;
            window.removeEventListener('storage', this.storageSyncListener);
        });
    }

    private broadcastUnreadCount(count: number): void {

        const payload: UnreadCountSyncPayload = { count, source: this.tabId, timestamp: Date.now() };
        this.crossTabChannel?.postMessage(payload);

        if (typeof localStorage === 'undefined') {
            return;
        }

        try {
            localStorage.setItem(this.CROSS_TAB_STORAGE_KEY, JSON.stringify(payload));
        } catch {
            return;
        }
    }

    private handleCrossTabPayload(payload: UnreadCountSyncPayload): void {

        if (!payload || payload.source === this.tabId) {
            return;
        }

        this.setUnreadCount(payload.count, false);
    }

    private broadcastAttention(count: number): void {

        const payload: QueueAttentionSyncPayload = { count, source: this.tabId, timestamp: Date.now() };
        this.attentionCrossTabChannel?.postMessage(payload);

        if (typeof localStorage === 'undefined') {
            return;
        }

        try {
            localStorage.setItem(this.ATTENTION_CROSS_TAB_STORAGE_KEY, JSON.stringify(payload));
        } catch {
            return;
        }
    }

    private handleAttentionCrossTabPayload(payload: QueueAttentionSyncPayload): void {

        if (!payload || payload.source === this.tabId) {
            return;
        }

        this.setQueueAttentionCount(payload.count, false);
    }

    private createTabId(): string {

        if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
            return crypto.randomUUID();
        }

        return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    }
}
